import axiosInstance from '../axiosInstance';
import { initializeDB } from '../config/db';
import {
    TRAINING_COURSE_STORE, TRAINING_QUIZ_STORE, TRAINING_SESSION_STORE, TRAINING_OUTBOX_STORE,
} from '../config/constants';
import { generateUUID } from '../utils/uuid';

// axiosInstance.baseURL уже включает /api — пути ниже идут без префикса /api.
//
// Офлайн-модель: create/addParticipant/markPassed работают БЕЗ сети — пишут оптимистично
// в IndexedDB и кладут операцию в очередь (trainingOutbox). При появлении сети очередь
// проигрывается на сервер (идемпотентно по uuid/clientId), затем сессия перечитывается
// с сервера. send/reset/remove/extend/close требуют сети (как и раньше).

// ─── READ: только из IndexedDB ─────────────────────────────────────────────────

export async function getLocalSessions() {
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_SESSION_STORE, 'readonly');
    const all = await tx.objectStore(TRAINING_SESSION_STORE).getAll();
    return (all || []).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

export async function getLocalSessionByUuid(uuid) {
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_SESSION_STORE, 'readonly');
    return await tx.objectStore(TRAINING_SESSION_STORE).get(uuid);
}

export async function getLocalCourses() {
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_COURSE_STORE, 'readonly');
    return (await tx.objectStore(TRAINING_COURSE_STORE).getAll()) || [];
}

export async function getLocalQuizzesForCourse(courseExternalId) {
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_QUIZ_STORE, 'readonly');
    const idx = tx.objectStore(TRAINING_QUIZ_STORE).index('byCourse');
    return (await idx.getAll(courseExternalId)) || [];
}

/** 555-123-4567 — так же, как форматирует бэкенд и как лежит в БД,
 *  чтобы оптимистичная строка не отличалась от прилетевшей с сервера. */
function formatUsPhone(digits) {
    const d = String(digits || '').replace(/\D/g, '');
    if (d.length !== 10) return digits || '';
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

// ─── LOCAL session helpers ─────────────────────────────────────────────────────

async function saveSessionToLocal(session) {
    if (!session?.uuid) return;
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_SESSION_STORE, 'readwrite');
    await tx.objectStore(TRAINING_SESSION_STORE).put(session);
    await tx.done;
}

async function deleteSessionFromLocal(uuid) {
    if (!uuid) return;
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_SESSION_STORE, 'readwrite');
    await tx.objectStore(TRAINING_SESSION_STORE).delete(uuid);
    await tx.done;
}

/**
 * Сохраняет СЕРВЕРНОЕ состояние сессии, не теряя локальных строк.
 *
 * Сервер авторитетен по своим участникам, но тех, кто ещё не доехал (_pendingSync)
 * или чья операция провалилась (_failed), в его ответе нет. Раньше здесь был голый
 * put — и такая строка молча стиралась при первом же поллинге (раз в 20 сек):
 * человек «пропадал из листа» без единого сообщения. Теперь мержим.
 */
async function mergeServerSession(fresh) {
    if (!fresh?.uuid) return null;

    const db = await initializeDB();
    const tx = db.transaction(TRAINING_SESSION_STORE, 'readwrite');
    const store = tx.objectStore(TRAINING_SESSION_STORE);
    const local = await store.get(fresh.uuid);

    let merged = fresh;
    const serverList = fresh.participantsList || [];

    if (local) {
        const localByClientId = new Map(
            (local.participantsList || []).filter(p => p.clientId).map(p => [p.clientId, p])
        );

        // Данные сервера авторитетны, НО пометку о провалившейся операции переносим.
        // Иначе для markPassed/updatePhone (участник на сервере уже есть) отметка
        // «прошёл» или новый номер откатились бы молча — тот самый случай, когда
        // изменение исчезает без объяснений.
        const serverMerged = serverList.map(sp => {
            const lp = sp.clientId ? localByClientId.get(sp.clientId) : null;
            return lp?._failed
                ? { ...sp, _failed: true, _failedLabel: lp._failedLabel, _error: lp._error }
                : sp;
        });

        // Строки, которых на сервере нет вовсе (не доехавшее добавление).
        const serverClientIds = new Set(serverList.map(p => p.clientId).filter(Boolean));
        const keptLocal = (local.participantsList || []).filter(p =>
            (p._pendingSync || p._failed) && p.clientId && !serverClientIds.has(p.clientId)
        );

        merged = {
            ...fresh,
            participantsList: [...serverMerged, ...keptLocal],
            participants: {
                ...(fresh.participants || {}),
                total: serverMerged.length + keptLocal.length,
            },
        };
    }

    await store.put(merged);
    await tx.done;
    return merged;
}

/** Помечает строку участника как непринятую сервером — вместо тихого отката. */
async function markParticipantFailed(sessionUuid, clientId, reason, label = 'Sync failed') {
    if (!sessionUuid || !clientId) return;
    await updateLocalSession(sessionUuid, (s) => {
        s.participantsList = (s.participantsList || []).map(p =>
            p.clientId === clientId
                ? {
                    ...p,
                    _pendingSync: false,
                    _failed: true,
                    _failedLabel: label,
                    _error: String(reason || 'Failed to sync'),
                }
                : p
        );
        return s;
    });
}

// read-modify-write одной сессии в IDB
async function updateLocalSession(uuid, mutator) {
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_SESSION_STORE, 'readwrite');
    const store = tx.objectStore(TRAINING_SESSION_STORE);
    const session = await store.get(uuid);
    if (session) {
        const next = mutator(session) || session;
        await store.put(next);
    }
    await tx.done;
}

// ─── OUTBOX (очередь офлайн-мутаций) ────────────────────────────────────────────

// Монотонный seq: гарантирует порядок create → addParticipant → markPassed даже когда
// несколько операций поставлены в одну миллисекунду (ключ opId — случайный uuid, по нему
// getAll вернул бы их вперемешку, и add мог бы проиграться раньше create → 404/висяк).
let enqueueCounter = 0;

async function enqueueOp(type, payload) {
    const db = await initializeDB();
    const seq = Date.now() * 1000 + (enqueueCounter++ % 1000);
    const op = { opId: generateUUID(), seq, type, payload, createdAt: new Date().toISOString(), tries: 0 };
    const tx = db.transaction(TRAINING_OUTBOX_STORE, 'readwrite');
    await tx.objectStore(TRAINING_OUTBOX_STORE).put(op);
    await tx.done;
    return op;
}

async function getOutboxOps() {
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_OUTBOX_STORE, 'readonly');
    const all = await tx.objectStore(TRAINING_OUTBOX_STORE).getAll();
    return (all || []).sort((a, b) => (a.seq || 0) - (b.seq || 0));
}

async function deleteOp(opId) {
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_OUTBOX_STORE, 'readwrite');
    await tx.objectStore(TRAINING_OUTBOX_STORE).delete(opId);
    await tx.done;
}

async function bumpOpTries(op) {
    const db = await initializeDB();
    const tx = db.transaction(TRAINING_OUTBOX_STORE, 'readwrite');
    await tx.objectStore(TRAINING_OUTBOX_STORE).put({ ...op, tries: (op.tries || 0) + 1 });
    await tx.done;
}

// Что именно не удалось — чтобы подпись в листе была честной, а не общей.
const FAILED_LABELS = {
    addParticipant: 'Not added',
    markPassed: 'Not marked passed',
    updatePhone: 'Phone not saved',
};

async function dropOp(op, reason) {
    console.warn('[TrainingOutbox] dropping poison op', op.type, op.opId, reason);
    // Операция выброшена — но изменение не должно откатиться молча.
    // Помечаем строку красным с причиной; тренер сам решит, что делать.
    if (FAILED_LABELS[op.type]) {
        await markParticipantFailed(
            op.payload?.sessionUuid,
            op.payload?.clientId,
            reason,
            FAILED_LABELS[op.type]
        );
    }
    await deleteOp(op.opId);
}

export async function pendingSyncCount() {
    return (await getOutboxOps()).length;
}

// ─── SYNC: проигрывание очереди ──────────────────────────────────────────────────

let draining = false;

/**
 * Проигрывает очередь на сервер. Идемпотентно: повтор любой операции безопасен
 * (сервер дедупит по uuid/clientId). Возвращает число успешно отправленных операций.
 */
export async function processOutbox() {
    if (draining) return 0;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return 0;
    draining = true;
    let done = 0;
    const touched = new Set();
    try {
        const ops = await getOutboxOps();
        for (const op of ops) {
            try {
                await applyOp(op);
                await deleteOp(op.opId);
                done++;
                if (op.payload?.uuid) touched.add(op.payload.uuid);
                if (op.payload?.sessionUuid) touched.add(op.payload.sessionUuid);
            } catch (e) {
                const status = e?.response?.status;
                if (status === 409) {
                    // конфликт/уже сделано — считаем успехом (идемпотентно)
                    await deleteOp(op.opId);
                    done++;
                } else if (status === 422) {
                    // постоянная ошибка валидации — иначе очередь встанет навсегда
                    await dropOp(op, e?.response?.data?.error || 'validation');
                } else {
                    // сеть / 5xx / 404 (предыдущий шаг ещё не доехал) — стоп, повторим позже.
                    // Но не вечно: после многих неудач выбрасываем «ядовитую» операцию.
                    if ((op.tries || 0) + 1 >= 8) {
                        await dropOp(op, 'too many retries (status ' + (status || 'network') + ')');
                        continue;
                    }
                    await bumpOpTries(op);
                    break;
                }
            }
        }
        // Перечитываем затронутые сессии с сервера (авторитетное состояние).
        for (const uuid of touched) {
            try {
                const fresh = (await axiosInstance.get(`/training-sessions/${uuid}`)).data;
                if (fresh?.uuid) await mergeServerSession(fresh);
            } catch (e) { /* offline again — оставляем оптимистичное состояние */ }
        }
    } finally {
        draining = false;
    }
    return done;
}

async function applyOp(op) {
    const p = op.payload || {};
    if (op.type === 'createSession') {
        const resp = await axiosInstance.post('/training-sessions', {
            uuid: p.uuid, courseId: p.courseId, language: p.language,
            lifetimeDays: p.lifetimeDays, notes: p.notes,
        });
        if (resp.data?.uuid) await mergeServerSession(resp.data);
    } else if (op.type === 'addParticipant') {
        await axiosInstance.post(`/training-sessions/${p.sessionUuid}/participants`, {
            clientId: p.clientId, type: p.type, refId: p.refId, phone: p.phone, displayName: p.displayName,
        });
    } else if (op.type === 'markPassed') {
        await axiosInstance.post(`/training-sessions/${p.sessionUuid}/participants/${p.ref}/complete-manual`, {
            completedAt: p.completedAt, note: p.note || null, signature: p.signature || null,
        });
    } else if (op.type === 'updatePhone') {
        await axiosInstance.patch(`/training-sessions/${p.sessionUuid}/participants/${p.pid}/phone`, {
            phone: p.phone,
        });
    }
}

// Авто-синк при возврате сети.
if (typeof window !== 'undefined') {
    window.addEventListener('online', () => { processOutbox(); });
}

// ─── MUTATIONS: офлайн-first ─────────────────────────────────────────────────────

export async function createSession({ courseId, language, lifetimeDays = 3, notes = null }) {
    const uuid = generateUUID();
    // Имя/языки курса берём локально, чтобы карточка сразу была осмысленной офлайн.
    let courseName = '';
    let courseExternalId = null;
    try {
        const courses = await getLocalCourses();
        const c = (courses || []).find(x => x.id === courseId);
        if (c) { courseName = c.course || ''; courseExternalId = c.externalId ?? null; }
    } catch (e) { /* ignore */ }

    const session = {
        uuid, courseId, courseExternalId, courseName,
        defaultLanguage: language, status: 'open',
        sentAt: null, createdAt: new Date().toISOString(),
        defaultLinkLifetimeDays: lifetimeDays, notes,
        participants: { total: 0, sent: 0, completed: 0 },
        participantsList: [],
        presentationLanguages: [],
        _pendingSync: true,
    };
    await saveSessionToLocal(session);
    await enqueueOp('createSession', { uuid, courseId, language, lifetimeDays, notes });
    processOutbox();
    return session;
}

export async function addParticipant(uuid, payload) {
    const clientId = generateUUID();
    const participant = {
        id: clientId,           // ключ DataGrid до синка; после синка прилетит серверный id
        clientId,
        displayName: payload.displayName || 'Participant',
        phoneRaw: payload.phone || '',
        phoneFormatted: formatUsPhone(payload.phone),
        kind: payload.type,
        refId: payload.refId,
        language: null,
        state: 'pending',
        smsStatus: 'pending',
        completedAt: null,
        completionType: null,
        score: null,
        _pendingSync: true,
    };
    await updateLocalSession(uuid, (s) => {
        s.participantsList = [...(s.participantsList || []), participant];
        s.participants = { ...(s.participants || {}), total: (s.participantsList.length) };
        return s;
    });
    await enqueueOp('addParticipant', {
        sessionUuid: uuid, clientId, type: payload.type, refId: payload.refId,
        phone: payload.phone, displayName: payload.displayName || null,
    });
    processOutbox();
    return participant;
}

/**
 * Ручной («очный») проход: тренер отмечает, что участник прошёл тренинг без квиза.
 * Работает офлайн — оптимистично помечаем completed и кладём в очередь.
 */
export async function markPassed(uuid, participant, { note = null, signature = null } = {}) {
    const ref = participant.clientId || participant.id;
    const completedAt = new Date().toISOString();
    await updateLocalSession(uuid, (s) => {
        s.participantsList = (s.participantsList || []).map(p => {
            const match = (p.clientId && p.clientId === participant.clientId) || (p.id === participant.id);
            if (!match) return p;
            return { ...p, state: 'completed', completedAt, completionType: 'manual', _pendingSync: true };
        });
        const completed = (s.participantsList || []).filter(p => !!p.completedAt).length;
        s.participants = { ...(s.participants || {}), completed };
        return s;
    });
    await enqueueOp('markPassed', { sessionUuid: uuid, ref: String(ref), completedAt, note, signature });
    processOutbox();
    return true;
}

/**
 * Смена номера участника прямо из листа — офлайн-first.
 * Номер уходит не только в участника, но и в карточку человека + внешнюю БД
 * (это делает бэкенд), поэтому в следующий раз вводить его не придётся.
 */
export async function updateParticipantPhone(uuid, participant, phone) {
    const clientId = participant.clientId || null;
    const serverId = typeof participant.id === 'number' ? participant.id : null;

    // Сначала решаем, как изменение доедет до сервера: участник ещё в очереди на
    // добавление — правим телефон прямо в его операции (уйдёт вместе с ним, без 404).
    const patchedQueued = await patchQueuedAddPhone(clientId, phone);
    // Если синхронизировать нечем (нет серверного id и нет операции) — не вешаем
    // «not synced» навсегда: такая строка и так помечена как непринятая.
    const willSync = patchedQueued || serverId !== null;

    // Оптимистично — сразу в IDB, чтобы лист обновился без сети. _pendingSync важен:
    // иначе мерж не защитит строку и поллинг через 20 сек молча вернёт старый номер.
    await updateLocalSession(uuid, (s) => {
        s.participantsList = (s.participantsList || []).map(p => {
            const match = (clientId && p.clientId === clientId) || (serverId !== null && p.id === serverId);
            if (!match) return p;
            return {
                ...p,
                phoneRaw: phone,
                phoneFormatted: formatUsPhone(phone),
                _pendingSync: willSync,
                ...(willSync ? { _failed: false, _failedLabel: undefined, _error: undefined } : {}),
            };
        });
        return s;
    });

    if (patchedQueued) {
        processOutbox();
        return { ok: true, queued: true };
    }

    if (serverId === null) return { ok: true, localOnly: true };

    await enqueueOp('updatePhone', { sessionUuid: uuid, pid: serverId, phone });
    processOutbox();
    return { ok: true };
}

/** Подменяет phone в ещё не отправленной операции добавления участника. */
async function patchQueuedAddPhone(clientId, phone) {
    if (!clientId) return false;
    const ops = await getOutboxOps();
    const pending = ops.find(o => o.type === 'addParticipant' && o.payload?.clientId === clientId);
    if (!pending) return false;

    const db = await initializeDB();
    const tx = db.transaction(TRAINING_OUTBOX_STORE, 'readwrite');
    await tx.objectStore(TRAINING_OUTBOX_STORE).put({
        ...pending,
        payload: { ...pending.payload, phone },
    });
    await tx.done;
    return true;
}

/** Сколько операций этой сессии ещё не ушло на сервер (для предупреждения при удалении). */
export async function pendingOpsCountForSession(uuid) {
    const ops = await getOutboxOps();
    return ops.filter(o => o.payload?.uuid === uuid || o.payload?.sessionUuid === uuid).length;
}

/**
 * Убирает сессию ТОЛЬКО с этого устройства. Сервер не трогаем: в Symfony сессия
 * и вся её история остаются. Обратно не приедет — trainingSessions не синкается
 * с сервера (см. config/syncConfig.jsx), стор наполняют только локальные мутации.
 */
export async function deleteLocalSession(uuid) {
    if (!uuid) return { ok: false };

    // Сначала выбрасываем её операции из очереди, иначе они позже уйдут на сервер
    // и создадут «призрак» уже удалённой с айпада сессии.
    const ops = await getOutboxOps();
    for (const op of ops) {
        if (op.payload?.uuid === uuid || op.payload?.sessionUuid === uuid) {
            await deleteOp(op.opId);
        }
    }
    await deleteSessionFromLocal(uuid);
    return { ok: true };
}

// ─── MUTATIONS: требуют сети (как и раньше) ──────────────────────────────────────

export async function removeParticipant(uuid, pid) {
    // Участник ещё не синканут (только локально + в очереди) — удаляем локально.
    if (typeof pid === 'string' && pid.includes('-')) {
        await updateLocalSession(uuid, (s) => {
            s.participantsList = (s.participantsList || []).filter(p => p.id !== pid && p.clientId !== pid);
            s.participants = { ...(s.participants || {}), total: (s.participantsList || []).length };
            return s;
        });
        return { ok: true, local: true };
    }
    const resp = await axiosInstance.delete(`/training-sessions/${uuid}/participants/${pid}`);
    const fresh = (await axiosInstance.get(`/training-sessions/${uuid}`)).data;
    await mergeServerSession(fresh);
    return resp.data;
}

export async function sendSession(uuid) {
    // Перед отправкой SMS убеждаемся, что сессия и участники доехали до сервера.
    await processOutbox();
    const resp = await axiosInstance.post(`/training-sessions/${uuid}/send`);
    if (resp.data?.session) {
        await mergeServerSession(resp.data.session);
    }
    return resp.data;
}

export async function resetParticipant(uuid, pid) {
    const resp = await axiosInstance.post(`/training-sessions/${uuid}/participants/${pid}/reset`);
    const fresh = (await axiosInstance.get(`/training-sessions/${uuid}`)).data;
    await mergeServerSession(fresh);
    return resp.data;
}

export async function extendParticipant(uuid, pid, days = 3) {
    const resp = await axiosInstance.post(`/training-sessions/${uuid}/participants/${pid}/extend`, { days });
    const fresh = (await axiosInstance.get(`/training-sessions/${uuid}`)).data;
    await mergeServerSession(fresh);
    return resp.data;
}

export async function closeSession(uuid) {
    // Сначала досинкаем очередь (чтобы ручные проходы успели уйти и сгенерить сертификаты).
    await processOutbox();
    const resp = await axiosInstance.post(`/training-sessions/${uuid}/close`);
    await deleteSessionFromLocal(uuid);
    return resp.data;
}

export async function fetchParticipantAnswers(uuid, pid) {
    const resp = await axiosInstance.get(`/training-sessions/${uuid}/participants/${pid}/answers`);
    return resp.data;
}

/**
 * История изменений номера этого человека: кто, с чего на что и когда.
 * Требует сети — офлайн отдаём пустой список (модалка просто не покажет блок).
 */
export async function fetchParticipantPhoneHistory(uuid, pid) {
    try {
        const resp = await axiosInstance.get(`/training-sessions/${uuid}/participants/${pid}/phone-history`);
        return resp.data?.history || [];
    } catch (e) {
        return [];
    }
}

/**
 * Polling-обновление одной сессии: проигрывает очередь, затем тянет свежее состояние.
 */
export async function refreshSessionFromServer(uuid) {
    try {
        await processOutbox();
        const fresh = (await axiosInstance.get(`/training-sessions/${uuid}`)).data;
        if (fresh?.uuid) {
            return await mergeServerSession(fresh);
        }
    } catch (e) {
        // тихо игнорируем — polling может фейлить из-за сети
    }
    return null;
}
